import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { dashboardApi, auditApi, usersApi, jobsApi, clientsApi } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  BarChart3,
  TrendingUp,
  Users,
  Briefcase,
  Clock,
  CheckCircle,
  Filter,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import DateRangePicker from '../components/DateRangePicker';
import DropReasonsTrend from '../components/reports/DropReasonsTrend';
import SlowestJoiningClients from '../components/reports/SlowestJoiningClients';
import OfferVsJoiningCard from '../components/analytics/OfferVsJoiningCard';
import ClientLocationHeatmap from '../components/jobs/ClientLocationHeatmap';
import IncentiveRateEditor from '../components/reports/IncentiveRateEditor';
import JoinedByIndustryCard from '../components/reports/JoinedByIndustryCard';
import SourceUploadsCard from '../components/reports/SourceUploadsCard';
import { canSeeRevenue } from '../lib/perms';

const RECRUITER_METRIC_TABS = [
  { key: 'joined_ctc', label: 'Joined CTC', accent: 'teal' },
  { key: 'joined', label: 'Joined Count', accent: 'emerald' },
];

const RECRUITER_PERIODS = [
  { value: 'all_time', label: 'All time' },
  { value: 'current_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
];

const stageBadgeClass = {
  sourced: 'badge-sourced',
  screening: 'badge-screening',
  client_submission: 'badge-submission',
  interview_scheduled: 'badge-interview',
  interview_selected: 'badge-selected',
  offer_released: 'badge-offer',
  joined: 'badge-joined',
  rejected: 'badge-rejected',
  dropped: 'badge-dropped',
};

const Reports = () => {
  const { user } = useAuth();
  const showRevenue = canSeeRevenue(user);
  const [stats, setStats] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [actions, setActions] = useState([]);
  const [userFilter, setUserFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [recruiterRevenuePeriod, setRecruiterRevenuePeriod] = useState('current_month');
  // Recruiter Revenue dimension: 'client' (default, by account) or
  // 'location' (legacy, by city). Both endpoints already exist server-side;
  // this is purely a UX toggle so admins can switch lenses without
  // remembering URLs. Persisted per-browser via localStorage so the user
  // doesn't have to re-pick their preferred view on every page reload.
  const [recruiterDimension, setRecruiterDimension] = useState(() => {
    try {
      const saved = localStorage.getItem('reports_recruiter_dimension');
      return saved === 'location' ? 'location' : 'client';
    } catch {
      return 'client';
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('reports_recruiter_dimension', recruiterDimension);
    } catch {
      // ignore — private browsing / storage full
    }
  }, [recruiterDimension]);
  // Bumped when the admin saves a new incentive rate so the heat-map below
  // refetches and re-renders the "+ ₹X earned" pills with the new % applied.
  const [incentiveReloadKey, setIncentiveReloadKey] = useState(0);

  // `joinBasis` lives on the Offer-vs-Joining card but should also drive
  // the Recruiter Revenue heatmap so totals reconcile across tabs. We
  // mirror it here via localStorage + a custom event the card emits.
  const [joinBasis, setJoinBasisLocal] = useState(() => {
    if (typeof window === 'undefined') return 'stage_event';
    return localStorage.getItem('ovj-join-basis') || 'stage_event';
  });
  useEffect(() => {
    const onChange = (e) => {
      const next = e?.detail?.basis;
      if (next === 'stage_event' || next === 'joining_date') {
        setJoinBasisLocal(next);
      }
    };
    window.addEventListener('joinBasis:changed', onChange);
    // Storage event fires across other tabs/windows when localStorage
    // changes — keeps a second open Reports tab in sync too.
    const onStorage = (e) => {
      if (e.key === 'ovj-join-basis' && e.newValue) {
        setJoinBasisLocal(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('joinBasis:changed', onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Wrap recruiter-client loader so the heatmap component (which knows
  // nothing about join_basis) passes our currently-active basis on every
  // re-fetch without needing a prop change.
  const recruiterClientLoader = useCallback(
    (metric, period) => jobsApi.recruiterClientMatrix(metric, period, joinBasis),
    [joinBasis],
  );

  // Click handler for the Recruiter × Client heatmap headers: resolve the
  // canonical client name back to its id and deep-link into Clients page
  // with `?view=<id>` so the existing client-detail dialog auto-opens.
  // Falls back to a name search filter if no exact match is found
  // (defensive — the new resolution path means every column should map).
  const navigate = useNavigate();
  const openClientByName = useCallback(async (name) => {
    if (!name || name === 'Direct / no client') return;
    try {
      // /api/clients returns {items, total, ...} — paginated shape.
      const res = await clientsApi.getAll({ search: name, page_size: 25 });
      const list = res?.data?.items || res?.data?.clients || (Array.isArray(res?.data) ? res.data : []);
      const target = (name || '').trim().toLowerCase();
      const exact = list.find((c) => (c.name || '').trim().toLowerCase() === target);
      const match = exact || list[0];
      if (match?.id) {
        navigate(`/clients?view=${match.id}`);
      } else {
        navigate(`/clients?search=${encodeURIComponent(name)}`);
      }
    } catch {
      navigate(`/clients?search=${encodeURIComponent(name)}`);
    }
  }, [navigate]);
  const [customRange, setCustomRange] = useState(null); // { from: Date, to: Date } or null
  const [loading, setLoading] = useState(true);

  const rangeIso = customRange
    ? {
        start_date: customRange.from.toISOString().slice(0, 10),
        end_date: customRange.to.toISOString().slice(0, 10),
      }
    : null;

  useEffect(() => {
    fetchData();
  }, []);

  // Re-fetch audit logs whenever any of the 3 filters change.
  useEffect(() => {
    if (loading) return; // skip the very first render — fetchData() handles it
    fetchAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilter, actionFilter, customRange]);

  const fetchData = async () => {
    try {
      const [statsRes, auditRes, usersRes, actionsRes] = await Promise.all([
        dashboardApi.getStats(),
        auditApi.getLogs(100).catch(() => ({ data: [] })),
        usersApi.getAll().catch(() => ({ data: [] })),
        auditApi.distinctActions().catch(() => ({ data: { actions: [] } })),
      ]);
      setStats(statsRes.data);
      setAuditLogs(auditRes.data || []);
      setUsers(usersRes.data || []);
      setActions(actionsRes.data?.actions || []);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  // Audit-only refetch for filter changes (avoids re-pulling stats + users + actions)
  const fetchAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const res = await auditApi.getLogs(
        100,
        userFilter === 'all' ? null : userFilter,
        actionFilter === 'all' ? null : actionFilter,
        rangeIso,
      );
      setAuditLogs(res.data || []);
    } catch (e) {
      toast.error('Failed to load audit logs');
    } finally {
      setAuditLoading(false);
    }
  };

  // Sort users for the dropdown (alphabetical)
  const userOptions = useMemo(
    () => [...users].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [users]
  );
  const selectedUser = useMemo(
    () => users.find((u) => u.id === userFilter),
    [users, userFilter]
  );

  // Convert snake_case actions into "Title Case" for display
  const prettyAction = (a) =>
    (a || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const handleRangeChange = ({ preset, customRange: cr }) => {
    if (cr && cr.from && cr.to) {
      setCustomRange(cr);
      return;
    }
    if (!preset) return;
    // Any preset chip → translate into a custom range so the audit-log filter
    // hits the start_date/end_date backend path consistently.
    const today = new Date();
    const from = new Date(today);
    if (preset === 'all') {
      setCustomRange(null);
      return;
    }
    if (preset === 'week') from.setDate(today.getDate() - 6);
    else if (preset === 'month') from.setDate(today.getDate() - 29);
    else if (preset === 'quarter') from.setDate(today.getDate() - 89);
    else if (preset === 'year') from.setDate(today.getDate() - 364);
    else if (preset === 'mtd') from.setDate(1);
    else if (preset === 'ytd') { from.setMonth(0); from.setDate(1); }
    from.setHours(0, 0, 0, 0);
    setCustomRange({ from, to: today });
  };

  // True when at least one filter is non-default — used to enable the "Clear all" button
  const hasAnyFilter = userFilter !== 'all' || actionFilter !== 'all' || !!customRange;
  const clearAllFilters = () => {
    setUserFilter('all');
    setActionFilter('all');
    setCustomRange(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  const pipelineData = stats?.pipeline_stats || {};
  const totalInPipeline = Object.values(pipelineData).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Reports & Analytics
        </h1>
        <p className="text-slate-600 mt-1">Track hiring metrics and audit trails</p>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="pipeline">Pipeline Analysis</TabsTrigger>
          <TabsTrigger value="drop-reasons" data-testid="reports-tab-drop-reasons">Drop Reasons</TabsTrigger>
          <TabsTrigger value="onboarding-tat" data-testid="reports-tab-onboarding-tat">Onboarding TAT</TabsTrigger>
          <TabsTrigger value="naukri-uploads" data-testid="reports-tab-naukri-uploads">Source Uploads</TabsTrigger>
          {showRevenue && (
            <TabsTrigger value="offer-vs-joining" data-testid="reports-tab-offer-vs-joining">Offer vs Joining</TabsTrigger>
          )}
          {showRevenue && (
            <TabsTrigger value="recruiter-revenue" data-testid="reports-tab-recruiter-revenue">Recruiter Revenue</TabsTrigger>
          )}
          <TabsTrigger value="audit">Audit Logs</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-6 h-6 text-teal-700" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-slate-900">{stats?.active_jobs || 0}</p>
                    <p className="text-sm text-slate-500">Active Jobs</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-700" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-slate-900">{stats?.total_candidates || 0}</p>
                    <p className="text-sm text-slate-500">Total Candidates</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-purple-700" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-slate-900">{totalInPipeline}</p>
                    <p className="text-sm text-slate-500">In Pipeline</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-slate-900">{pipelineData.joined || 0}</p>
                    <p className="text-sm text-slate-500">Joined</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Conversion Funnel */}
          <Card>
            <CardHeader>
              <CardTitle>Hiring Funnel</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(pipelineData).map(([stage, count], index) => {
                  const percentage = totalInPipeline > 0 ? (count / totalInPipeline) * 100 : 0;
                  return (
                    <div key={stage} className="flex items-center gap-4">
                      <div className="w-32">
                        <Badge className={stageBadgeClass[stage] || 'bg-slate-100'}>
                          {stage.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="flex-1">
                        <div className="h-6 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-teal-600 rounded-full transition-all duration-500"
                            style={{ width: `${Math.max(percentage, 2)}%` }}
                          />
                        </div>
                      </div>
                      <div className="w-16 text-right">
                        <span className="font-semibold text-slate-900">{count}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Pipeline Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(pipelineData).map(([stage, count]) => (
                  <div
                    key={stage}
                    className="p-4 bg-slate-50 rounded-xl text-center"
                  >
                    <p className="text-3xl font-bold text-slate-900">{count}</p>
                    <Badge className={`mt-2 ${stageBadgeClass[stage] || 'bg-slate-100'}`}>
                      {stage.replace('_', ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-4xl font-bold text-teal-700">
                  {totalInPipeline > 0 
                    ? Math.round(((pipelineData.interview_scheduled || 0) + (pipelineData.interview_selected || 0)) / totalInPipeline * 100)
                    : 0}%
                </p>
                <p className="text-sm text-slate-500 mt-2">Interview Rate</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-4xl font-bold text-teal-700">
                  {totalInPipeline > 0 
                    ? Math.round((pipelineData.offer_released || 0) / totalInPipeline * 100)
                    : 0}%
                </p>
                <p className="text-sm text-slate-500 mt-2">Offer Rate</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-4xl font-bold text-teal-700">
                  {totalInPipeline > 0 
                    ? Math.round((pipelineData.joined || 0) / totalInPipeline * 100)
                    : 0}%
                </p>
                <p className="text-sm text-slate-500 mt-2">Join Rate</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Drop Reasons Tab */}
        <TabsContent value="drop-reasons" className="space-y-6">
          <DropReasonsTrend />
        </TabsContent>

        {/* Onboarding TAT Tab — ranks clients by how slowly offers turn into
            joinings. Manual offer_letter_date is the real benchmark; auto
            stage-change timestamp is the fallback. */}
        <TabsContent value="onboarding-tat" className="space-y-6">
          <SlowestJoiningClients />
        </TabsContent>

        {/* Source Uploads Tab — recruiter-wise count of bulk-uploaded
            resumes split by source portal (Naukri / LinkedIn / Shine /
            Foundit / Other). Powers attribution conversations for
            portal-sourced candidates. */}
        <TabsContent value="naukri-uploads" className="space-y-6" data-testid="reports-naukri-uploads">
          <SourceUploadsCard />
        </TabsContent>

        <TabsContent value="offer-vs-joining" className="space-y-6">
          {showRevenue && (
            <OfferVsJoiningCard
              variant="full"
              period={rangeIso ? 'custom' : 'month'}
              startDate={rangeIso?.start_date}
              endDate={rangeIso?.end_date}
              testid="reports-ovj"
            />
          )}
        </TabsContent>

        {/* Recruiter Revenue tab — mirrors the Client × Location heat-map on
            the Jobs page but pivots rows to recruiters. Surfaces who
            contributed how much joined CTC across which client locations,
            which makes incentive math + revenue concentration trivial to
            eyeball. RBAC is enforced server-side so managers only see
            their subordinates. */}
        <TabsContent value="recruiter-revenue" className="space-y-6" data-testid="reports-recruiter-revenue">
          {showRevenue && (
            <>
              <IncentiveRateEditor
                onSaved={() => setIncentiveReloadKey((k) => k + 1)}
              />
              {/* Basis indicator — kept in sync with Offer vs Joining card
                  so the totals you see here always reconcile with that
                  tab's numbers. Tap to flip both views to the other basis. */}
              <div
                className="flex items-center gap-2 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5 w-fit"
                data-testid="recruiter-revenue-basis-indicator"
              >
                <span>Joins counted by:</span>
                <button
                  type="button"
                  className={`px-2 py-0.5 rounded ${joinBasis === 'joining_date' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 hover:bg-slate-100'}`}
                  onClick={() => {
                    setJoinBasisLocal('joining_date');
                    try { localStorage.setItem('ovj-join-basis', 'joining_date'); } catch { /* ignore */ }
                    try { window.dispatchEvent(new CustomEvent('joinBasis:changed', { detail: { basis: 'joining_date' } })); } catch { /* ignore */ }
                  }}
                  data-testid="recruiter-revenue-basis-joining"
                >
                  Joining date
                </button>
                <button
                  type="button"
                  className={`px-2 py-0.5 rounded ${joinBasis === 'stage_event' ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 hover:bg-slate-100'}`}
                  onClick={() => {
                    setJoinBasisLocal('stage_event');
                    try { localStorage.setItem('ovj-join-basis', 'stage_event'); } catch { /* ignore */ }
                    try { window.dispatchEvent(new CustomEvent('joinBasis:changed', { detail: { basis: 'stage_event' } })); } catch { /* ignore */ }
                  }}
                  data-testid="recruiter-revenue-basis-stage"
                >
                  Stage event
                </button>
              </div>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                {/* Dimension toggle: Client (default) vs Location */}
                <div
                  className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs font-medium"
                  data-testid="recruiter-revenue-dimension-toggle"
                  role="tablist"
                  aria-label="Recruiter revenue dimension"
                >
                  {[
                    { key: 'client', label: 'By Client' },
                    { key: 'location', label: 'By Location' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      role="tab"
                      aria-selected={recruiterDimension === opt.key}
                      onClick={() => setRecruiterDimension(opt.key)}
                      className={`px-3 py-1.5 transition ${
                        recruiterDimension === opt.key
                          ? 'bg-slate-900 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                      data-testid={`recruiter-revenue-dim-${opt.key}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <Select value={recruiterRevenuePeriod} onValueChange={setRecruiterRevenuePeriod}>
                  <SelectTrigger className="w-[180px]" data-testid="recruiter-revenue-period">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RECRUITER_PERIODS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {recruiterDimension === 'client' ? (
                <ClientLocationHeatmap
                  key={`rec-rev-client-${incentiveReloadKey}-${joinBasis}`}
                  period={recruiterRevenuePeriod}
                  loader={recruiterClientLoader}
                  title="Recruiter × Client"
                  rowLabel="Recruiter"
                  rowNoun="recruiters"
                  colNoun="clients"
                  metricTabs={RECRUITER_METRIC_TABS}
                  defaultMetric="joined_ctc"
                  testid="recruiter-client-heatmap"
                  onColumnClick={openClientByName}
                />
              ) : (
                <ClientLocationHeatmap
                  key={`rec-rev-loc-${incentiveReloadKey}`}
                  period={recruiterRevenuePeriod}
                  loader={jobsApi.recruiterLocationMatrix}
                  title="Recruiter × Location"
                  rowLabel="Recruiter"
                  rowNoun="recruiters"
                  colNoun="locations"
                  metricTabs={RECRUITER_METRIC_TABS}
                  defaultMetric="joined_ctc"
                  testid="recruiter-location-heatmap"
                />
              )}
              {/* Companion view: same joined CTC pool, sliced by client
                  industry instead of recruiter — answers "which verticals
                  are driving placement revenue this period?" */}
              <JoinedByIndustryCard />
            </>
          )}
        </TabsContent>

        {/* Audit Logs Tab */}
        <TabsContent value="audit">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
              <CardTitle>Audit Trail</CardTitle>
              <div className="flex items-center gap-2 flex-wrap" data-testid="audit-filter-bar">
                <Filter className="w-4 h-4 text-slate-500" />
                {/* User filter */}
                <Select value={userFilter} onValueChange={setUserFilter}>
                  <SelectTrigger
                    className="h-9 w-[200px]"
                    data-testid="audit-user-filter-trigger"
                  >
                    <SelectValue placeholder="All users" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="all" data-testid="audit-user-filter-all">
                      All users
                    </SelectItem>
                    {userOptions.map((u) => (
                      <SelectItem
                        key={u.id}
                        value={u.id}
                        data-testid={`audit-user-filter-${u.id}`}
                      >
                        <div className="flex flex-col leading-tight py-0.5">
                          <span className="text-sm font-medium">{u.name || u.email}</span>
                          <span className="text-[10px] text-slate-500 capitalize">
                            {(u.role || '').replace('_', ' ')}{u.email ? ` · ${u.email}` : ''}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Action filter */}
                <Select value={actionFilter} onValueChange={setActionFilter}>
                  <SelectTrigger
                    className="h-9 w-[200px]"
                    data-testid="audit-action-filter-trigger"
                  >
                    <SelectValue placeholder="All actions" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    <SelectItem value="all" data-testid="audit-action-filter-all">
                      All actions
                    </SelectItem>
                    {actions.map((a) => (
                      <SelectItem
                        key={a}
                        value={a}
                        data-testid={`audit-action-filter-${a}`}
                      >
                        {prettyAction(a)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Date range picker — same component used by /analytics, /my-team, /my-performance */}
                <DateRangePicker
                  preset={customRange ? null : 'all'}
                  customRange={customRange}
                  onChange={handleRangeChange}
                  testid="audit-range"
                />

                {hasAnyFilter ? (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 px-2 py-1 rounded hover:bg-slate-100 transition"
                    data-testid="audit-clear-all-filters"
                    title="Reset all filters"
                  >
                    <X className="w-3.5 h-3.5" />
                    Clear all
                  </button>
                ) : null}
                <span className="text-xs text-slate-500 font-mono ml-1" data-testid="audit-row-count">
                  {auditLoading ? 'Loading…' : `${auditLogs.length} row${auditLogs.length === 1 ? '' : 's'}`}
                </span>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Active-filter banner — shows currently-applied filter chips */}
              {hasAnyFilter ? (
                <div
                  className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900 flex items-center gap-2 flex-wrap"
                  data-testid="audit-active-filter-banner"
                >
                  <Filter className="w-3.5 h-3.5 flex-shrink-0" />
                  <span>Showing</span>
                  {selectedUser ? (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 rounded"
                      data-testid="audit-active-chip-user"
                    >
                      User: <strong>{selectedUser.name || selectedUser.email}</strong>
                    </span>
                  ) : null}
                  {actionFilter !== 'all' ? (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 rounded"
                      data-testid="audit-active-chip-action"
                    >
                      Action: <strong>{prettyAction(actionFilter)}</strong>
                    </span>
                  ) : null}
                  {customRange ? (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 rounded"
                      data-testid="audit-active-chip-range"
                    >
                      {format(customRange.from, 'd MMM')} – {format(customRange.to, 'd MMM yyyy')}
                    </span>
                  ) : null}
                </div>
              ) : null}
              {/* Long audit log lists used to grow the page indefinitely.
                  Pass `containerClassName` so the Table's wrapper div becomes
                  the scrolling ancestor — required for the sticky <thead>
                  below to stay pinned while rows scroll. */}
              <Table containerClassName="max-h-[calc(100vh-340px)] min-h-[320px]" data-testid="audit-table-scroll">
                <TableHeader className="sticky top-0 z-10 bg-white shadow-sm">
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Timestamp</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                        No audit logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    auditLogs.map((log) => (
                      <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                        <TableCell className="text-slate-700" data-testid={`audit-row-user-${log.id}`}>
                          {/* New audit entries carry user_name/email/role; pre-iter_29 entries
                              don't — fall back gracefully to "Unknown user" so old rows still
                              render readable. */}
                          <div className="flex flex-col leading-tight">
                            <span className="text-sm font-medium text-slate-800">
                              {log.user_name || (log.user_email || 'Unknown user')}
                            </span>
                            {log.user_email && log.user_name ? (
                              <span className="text-[11px] text-slate-500 truncate max-w-[160px]">
                                {log.user_email}
                              </span>
                            ) : null}
                            {log.user_role ? (
                              <span className="text-[10px] text-slate-400 capitalize">
                                {log.user_role.replace('_', ' ')}
                              </span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {log.action.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-slate-600 max-w-md truncate">
                          {log.details}
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm')}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Reports;
